import { useMemo } from 'react';
import { logger } from '@fiverr-private/obs';
import { CollectionMenuOption, CollectionMinimal, RawCollection, ResourceType } from '../types';
import { getCollectionOptions } from '../utils/getCollectionOptions/getCollectionOptions';
import { isOptimisticId } from '../store/utils';
import { useAddToCollection } from './useAddToCollection';
import { resourceTypeToLookupKey, useCollections } from './useCollections';
import { useRemoveFromCollection } from './useRemoveFromCollection';

interface UseResourceCollectionsProps {
    resourceType: ResourceType;
    resourceId: string;
    enabled?: boolean;
    initialCollections?: RawCollection[];

    /**
     * Which collection are we currently viewing. Used to list it first.
     */
    currentCollectionId?: string;
}

interface UseResourceCollectionsResult {
    /**
     * The list of all the collections with a `checked` property indicating
     * whether the resource is in the collection.
     */
    collectionOptions: CollectionMenuOption[];

    /**
     * The list of collections the resource is in. Minimal subset of a Collection object.
     */
    inCollections: CollectionMinimal[];

    /**
     * The list of collection ids the resource is in.
     */
    inCollectionIds: string[];

    /**
     * Adds the resource to the collection. Updates cache if a provider is available.
     */
    addToCollection: (collectionId: string) => Promise<void>;

    /**
     * Removes the resource from the collection. Updates cache if a provider is available.
     */
    removeFromCollection: (collectionId: string) => Promise<void>;

    isLoading: boolean;
}

/**
 * Hook to get the collections a resource is in and add/remove it from collections.
 */
export const useResourceCollections = ({
    resourceType,
    resourceId,
    enabled,
    currentCollectionId,
    initialCollections,
}: UseResourceCollectionsProps): UseResourceCollectionsResult => {
    const { collections, collectionsLookup, collectionsById, isLoading } = useCollections({
        enabled,
        initialCollections,
    });
    const lookupKey = resourceTypeToLookupKey[resourceType];
    const edgeMap = collectionsLookup[lookupKey]?.[resourceId];
    const { addToCollection: addToCollectionInternal } = useAddToCollection(resourceType, resourceId);
    const { removeFromCollection: removeFromCollectionInternal } = useRemoveFromCollection();

    const inCollectionIds = useMemo(() => {
        if (!edgeMap) {
            return [];
        }

        return Object.keys(edgeMap).filter((collectionId) => !edgeMap[collectionId].pendingRemoval);
    }, [edgeMap]);

    const collectionIdsPendingSelect = useMemo(() => {
        if (!edgeMap) {
            return [];
        }

        return Object.keys(edgeMap).filter((collectionId) => isOptimisticId(edgeMap[collectionId].itemId));
    }, [edgeMap]);

    const collectionIdsPendingUnselect = useMemo(() => {
        if (!edgeMap) {
            return [];
        }

        return Object.keys(edgeMap).filter((collectionId) => edgeMap[collectionId].pendingRemoval);
    }, [edgeMap]);

    const addToCollection = async (collectionId: string) => {
        const collection = collectionsById[collectionId];

        if (!collection) {
            logger.error(`Failed to add to collection. Collection with id ${collectionId} not found`);
            throw new Error(`Failed to add to collection. Collection with id ${collectionId} not found`);
        }

        return addToCollectionInternal({
            collectionId,
            collectionName: collection.name,
            collectionSlug: collection.slug,
        });
    };

    const removeFromCollection = async (collectionId: string) => {
        const edge = edgeMap[collectionId];

        if (!edge) {
            logger.error(`Failed to remove from collection. Collection with id ${collectionId} not found`);
            throw new Error(`Failed to remove from collection. Collection with id ${collectionId} not found`);
        }

        const { itemId, collectionName } = edgeMap[collectionId];

        return removeFromCollectionInternal({ itemId, collectionName });
    };

    const collectionOptions = useMemo(() => {
        if (!collections) {
            return [];
        }

        return getCollectionOptions({
            collections,
            selectedCollectionIds: inCollectionIds,
            collectionIdsPendingSelect,
            collectionIdsPendingUnselect,
            currentCollectionId,
        });
    }, [collections, inCollectionIds, collectionIdsPendingSelect, collectionIdsPendingUnselect, currentCollectionId]);

    const inCollections = useMemo(
        () =>
            collectionOptions
                .filter((collection) => collection.checked)
                .map((collection) => ({ id: collection.id, name: collection.name })),
        [collectionOptions]
    );

    return {
        collectionOptions,
        inCollections,
        inCollectionIds,
        addToCollection,
        removeFromCollection,
        isLoading,
    };
};
